import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DownloadDropdown } from "../Components/DownloadDropdown/DownloadDropdown";
import { FilterBar } from "../Components/FilterBar/FilterBar";
import { FullPageSpinner } from "../Components/Spinner";
import { BPMTable } from "../Components/StickyTable/BPMTable";
import "./MetaLiftTable.scss";
import { useSetError } from "../redux/modals";
import { useStateFunction } from "../utils/hooks/useData";
import { exportToExcel } from "../utils/download-utils";
import { RouteComponentProps } from "@reach/router";
import { awaitJSON, MetaLambdaFetch } from "../utils/fetch-utils";
import { formatMoney } from "@blisspointmedia/bpm-types/dist/formatUtils";

const baseHeaders = [
  {
    name: "test_name",
    label: "Test Name",
    flex: 1,
  },
  {
    name: "start_date",
    label: "Start",
    flex: 1,
  },
  {
    name: "end_date",
    label: "End",
    flex: 1,
  },
  {
    name: "budget",
    label: "Budget",
    flex: 1,
  },
  {
    name: "cells_display",
    label: "Cells",
    flex: 1,
  },
  {
    name: "objective_source",
    label: "Objective and Source",
    flex: 1,
  },
  {
    name: "created",
    label: "Created On",
    flex: 1,
  },
  {
    name: "creator",
    label: "Creator",
    flex: 1,
  },
  {
    name: "study_id",
    label: "Test ID",
    flex: 1,
  },
  {
    name: "powered_display",
    label: "Powered",
    flex: 1,
  },
  {
    name: "total_power_calc_results_display",
    label: "Total Power Calc Results",
    flex: 1.5,
    className: "metaLiftPowerCalcColumn",
    renderer: (row: MetaLiftTableRow) => (
      <div className="metaLiftPowerCalcText">{row.total_power_calc_results_display || "-"}</div>
    ),
  },
  {
    name: "cell_level_power_calc_results_display",
    label: "Cell Level Power Calc Results",
    flex: 1.8,
    className: "metaLiftPowerCalcColumn",
    renderer: (row: MetaLiftTableRow) => (
      <div className="metaLiftPowerCalcText">
        {row.cell_level_power_calc_results_display || "-"}
      </div>
    ),
  },
];

interface SpendAmount {
  value: number;
  formatted?: string;
}

interface TotalPowerCalcResults {
  title?: string;
  total_minimum_required_spend?: SpendAmount;
  expected_spend?: SpendAmount;
}

interface CellLevelPowerResult {
  cell_id?: string;
  cell_label?: string;
  cell_name?: string;
  minimum_required_spend?: SpendAmount;
  expected_spend?: SpendAmount;
}

interface CellToCellComparison {
  title?: string;
  minimum_required_spend?: SpendAmount;
  expected_spend?: SpendAmount;
}

interface CellLevelPowerCalcResults {
  title?: string;
  cells?: CellLevelPowerResult[];
  cell_to_cell_comparison?: CellToCellComparison | null;
}

const parseJsonField = <T extends object>(value: unknown): T | null => {
  if (value == null) {
    return null;
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }
  if (typeof value === "object") {
    return value as T;
  }
  return null;
};

const formatSpend = (spend?: SpendAmount): string => {
  if (!spend) {
    return "$0.00";
  }
  if (spend.formatted) {
    return spend.formatted;
  }
  return formatMoney(Number(spend.value || 0));
};

const formatTotalPowerCalcResults = (rawValue: unknown): string => {
  const parsed = parseJsonField<TotalPowerCalcResults>(rawValue);
  if (!parsed) {
    return "-";
  }
  return [
    "Total Power Calc Results",
    `Total Minimum Required Spend: ${formatSpend(parsed.total_minimum_required_spend)}`,
    `Expected Spend: ${formatSpend(parsed.expected_spend)}`,
  ].join("\n");
};

const formatCellLevelPowerCalcResults = (rawValue: unknown): string => {
  const parsed = parseJsonField<CellLevelPowerCalcResults>(rawValue);
  if (!parsed) {
    return "-";
  }

  const lines: string[] = ["Cell Level Power Calc Results"];
  const cells = parsed.cells ?? [];

  cells.forEach((cell, index) => {
    const cellLabel = cell.cell_label || `Cell ${String.fromCharCode(65 + index)}`;
    lines.push(cellLabel);
    lines.push(`Minimum Required Spend: ${formatSpend(cell.minimum_required_spend)}`);
    lines.push(`Expected Spend: ${formatSpend(cell.expected_spend)}`);
  });

  if (parsed.cell_to_cell_comparison) {
    lines.push("Cell-to-Cell Comparison");
    lines.push(
      `Minimum Required Spend: ${formatSpend(
        parsed.cell_to_cell_comparison.minimum_required_spend
      )}`
    );
    lines.push(`Expected Spend: ${formatSpend(parsed.cell_to_cell_comparison.expected_spend)}`);
  }

  return lines.join("\n");
};

const normalizePowered = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }
  return null;
};

export interface MetaLiftTableRow {
  study_id: string;
  company: string;
  test_name: string;
  start_date: string;
  end_date: string;
  budget: string;
  cells_json: string;
  cells_display: string;
  objectives_json: string;
  objective_source: string;
  created: string;
  creator: string;
  powered?: boolean | null;
  powered_display?: string;
  total_power_calc_results?: unknown;
  cell_level_power_calc_results?: unknown;
  total_power_calc_results_display?: string;
  cell_level_power_calc_results_display?: string;
}

export type MetaLiftTableMode = "drafts" | "published";

interface MetaLiftTableProps {
  company: string;
  mode: MetaLiftTableMode;
  onRowClick?: (row: MetaLiftTableRow) => void;
  refreshKey?: number;
}

const MetaLiftTable = ({
  company,
  mode,
  onRowClick,
  refreshKey = 0,
}: RouteComponentProps & MetaLiftTableProps): JSX.Element => {
  const setError = useSetError();
  const [tableRows, setTableRows] = useState<MetaLiftTableRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isDrafts = mode === "drafts";
  const modeLabel = isDrafts ? "drafts" : "published tests";
  const exportFileName = isDrafts ? "meta_lift_drafts" : "meta_lift_published";

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        let data: MetaLiftTableRow[] = [];

        if (isDrafts) {
          // TODO: Fetch meta lift drafts from postgres table
        } else {
          const res = await MetaLambdaFetch("/getPublishedMetaLiftTests", { params: { company } });
          data = await awaitJSON<MetaLiftTableRow[]>(res);
        }

        data = data.map(row => ({
          ...row,
          powered: normalizePowered(row.powered),
          start_date: row.start_date ? row.start_date.split("T")[0] : row.start_date,
          end_date: row.end_date ? row.end_date.split("T")[0] : row.end_date,
          created: row.created ? row.created.split("T")[0] : row.created,
          budget: row.budget ? `${formatMoney(Number(row.budget), 0)}` : row.budget,
          powered_display: normalizePowered(row.powered) ? "Powered" : "Not Powered",
          total_power_calc_results_display: formatTotalPowerCalcResults(
            row.total_power_calc_results
          ),
          cell_level_power_calc_results_display: formatCellLevelPowerCalcResults(
            row.cell_level_power_calc_results
          ),
        }));
        setTableRows(data);
      } catch (error: any) {
        setError({
          message: `Failed to load ${modeLabel}: ${error.message}`,
          reportError: error,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [company, mode, isDrafts, modeLabel, setError, refreshKey]);
  const [filter, setFilter] = useStateFunction<(line: any) => boolean>(() => true);

  const tableHeaders = useMemo(() => {
    return baseHeaders;
  }, []);

  const filteredData = useMemo(() => {
    return tableRows?.filter(filter) || [];
  }, [tableRows, filter]);

  const handleExcelDownload = useCallback(() => {
    exportToExcel(filteredData, exportFileName);
  }, [filteredData, exportFileName]);

  const handleRowClick = useCallback(
    (row: MetaLiftTableRow) => {
      if (isDrafts && onRowClick) {
        onRowClick(row);
      }
    },
    [isDrafts, onRowClick]
  );

  return (
    <div className="metaLiftTableWrapper">
      {isLoading ? (
        <FullPageSpinner />
      ) : tableRows.length === 0 ? (
        <div className="metaLiftTable">
          <div className="metaLiftTableContainer">
            <div className="metaLiftTableHeader">
              <div className="metaLiftTableHeaderTitle">
                No {modeLabel} available for {company}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="metaLiftTable">
          <div className="metaLiftTableContainer">
            <div className="metaLiftTableHeader">
              <FilterBar
                hasAdvanced
                onFilter={setFilter}
                lines={tableRows}
                options={tableHeaders}
              />
              <DownloadDropdown
                size="sm"
                onClickOptions={[handleExcelDownload]}
                menuOptions={["XLSX"]}
              />
            </div>
            <div className="metaLiftTableContentContainer">
              <div
                className={`metaLiftTableContent ${
                  isDrafts ? "metaLiftTableContentClickable" : ""
                }`}
              >
                <BPMTable
                  data={filteredData}
                  headers={tableHeaders}
                  filterBar={false}
                  emptyDisplay="-"
                  rowHeight={220}
                  {...(isDrafts && onRowClick && { onRowClick: handleRowClick })}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaLiftTable;
